/**
 * EmptyState — renders pre-search and post-search empty state variants.
 *
 * Pre-search  : nodata illustration + search icon + "Ingresa criterios y presiona Buscar"
 * Post-search : nodata illustration + info icon  + "Sin resultados"
 *
 * @module components/list/EmptyState
 * @see FMPV-MVWC-E002-S004 I-004
 */
import React from 'react';
import nodataImg from '../../assets/nodata.svg';

export interface EmptyStateProps {
  hasSearched: boolean;
  /**
   * compact=true: removes outer padding — for use inside siesa-ui-kit Table whose
   * empty-state wrapper already provides py-12 (48px) spacing on each side.
   */
  compact?: boolean;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

export function EmptyState({ hasSearched, compact = false, t }: EmptyStateProps) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  return (
    <div
      data-testid={hasSearched ? 'empty-state-post-search' : 'empty-state-pre-search'}
      className={`flex flex-col items-center gap-4 ${compact ? '' : 'py-12 px-10'}`}
    >
      <img
        src={nodataImg}
        alt={hasSearched ? tt('emptyNoResultsTitle', 'Sin resultados') : tt('emptyEnterCriteriaAlt', 'Ingresa criterios')}
        className="w-44 h-44 object-contain"
      />

      <div className="flex items-start gap-2.5 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-xl px-4 py-3.5 max-w-[420px] w-full">
        <span className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5">
          {hasSearched ? (
            <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
          ) : (
            <svg width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <circle cx={11} cy={11} r={8} />
              <path d="m21 21-4.35-4.35" />
            </svg>
          )}
        </span>

        <div>
          <strong className="block text-sm font-semibold text-blue-700 dark:text-blue-300">
            {hasSearched ? tt('emptyNoResultsTitle', 'Sin resultados') : tt('emptyEnterCriteriaTitle', 'Ingresa criterios y presiona Buscar')}
          </strong>
          <span className="block text-xs text-blue-600/80 dark:text-blue-400/80 mt-0.5">
            {hasSearched
              ? tt('emptyNoResultsDesc', 'No hay registros que coincidan con los filtros.')
              : tt('emptyEnterCriteriaDesc', 'Usa los filtros del panel superior para encontrar registros.')}
          </span>
        </div>
      </div>
    </div>
  );
}
